
import { logger } from '@/utils/production-logger';

// STEP 1: Message Save Deduplication - Track active saves
const activeSaves = new Map<string, Promise<boolean>>();

export class DeduplicationService {
  /**
   * Check if a message save is already in progress
   */
  static isMessageSaveActive(messageId: string): boolean {
    return activeSaves.has(messageId);
  }

  /**
   * Get active save promise for a message
   */
  static getActiveSave(messageId: string): Promise<boolean> | undefined {
    return activeSaves.get(messageId);
  }

  /**
   * Track a save operation
   */
  static trackSave(messageId: string, savePromise: Promise<boolean>): void {
    activeSaves.set(messageId, savePromise);
  }

  /**
   * Clean up tracking when save is done
   */
  static cleanupSave(messageId: string): void {
    activeSaves.delete(messageId);
  }

  /**
   * Execute save with deduplication protection
   */
  static async executeSaveWithDeduplication<T>(
    messageId: string,
    saveOperation: () => Promise<T>
  ): Promise<T> {
    if (activeSaves.has(messageId)) {
      console.log('[DEDUP] Message save already in progress:', { messageId });
      return await activeSaves.get(messageId)! as T;
    }

    const savePromise = saveOperation();
    activeSaves.set(messageId, savePromise as Promise<boolean>);

    try {
      const result = await savePromise;
      return result;
    } finally {
      activeSaves.delete(messageId);
    }
  }
}
